import * as React from "react";

import { Box, Link, Typography } from "@material-ui/core";
import { useMutation } from "react-query";
import * as Redux from "react-redux";

import valuePropIcon1 from "client/assets/cash-marketing-icon1.svg";
import valuePropIcon2 from "client/assets/cash-marketing-icon2.svg";
import valuePropIcon3 from "client/assets/cash-marketing-icon3.svg";
import { MopButton } from "client/components";
import { useUserRoute } from "client/context/router-context";
import { useMerchantModel, useRunOnce, useTranslation } from "client/hooks";
import { logAmplitudeEvent } from "client/lib/amplitude";
import { CASH_PROGRAM_RULES_LINK } from "client/pages/CashPay/CashPayValueProps";
import { UserService } from "client/services";
import {
  errorActions,
  formCompletionActions,
  RootState,
  userActions,
} from "client/store";
import { routes } from "client/utils/route-utils";

import { StatusLabel } from "../StatusLabel";
import { StatusStep } from "../StatusStep";
import { useStyles } from "../styles";
import { Status } from "../types";
import type { StatusMap } from "../types";

const AddCashPayStepValueProp: React.FC<{
  icon: React.ReactNode;
  body: string | React.ReactNode;
  header: string;
}> = ({ header, body, icon }) => {
  const classes = useStyles();
  return (
    <Box className={classes.addCashPayStepValuePropFactWrapper} mb={1}>
      <Box mr={2}>{icon}</Box>
      <Box>
        <Typography
          variant="body1"
          className={classes.addCashPayStepValuePropFactHeader}
        >
          {header}
        </Typography>
        <Typography
          className={classes.addCashPayStepValuePropFactBody}
          variant="body2"
        >
          {body}
        </Typography>
      </Box>
    </Box>
  );
};

export const determineCurrentState = (cashPayFormComplete: boolean): Status =>
  cashPayFormComplete ? "Done" : "InProgress";

export const AddCashPayStep: React.FC = () => {
  const { setRoute } = useUserRoute();
  const t = useTranslation();
  const classes = useStyles();
  const dispatch = Redux.useDispatch();
  const { formatMerchantForCashPayInfo } = useMerchantModel();

  const formCompletionState = Redux.useSelector(
    ({ formCompletion }: RootState) => formCompletion
  );

  const currentState = determineCurrentState(formCompletionState.cashPayForm);

  const valueProps = [
    {
      iconSrc: valuePropIcon1,
      headerText: t("uiCashPayValuePropFactHeader1"),
      bodyText: t("uiCashPayValuePropFactBody1"),
    },
    {
      iconSrc: valuePropIcon2,
      headerText: t("uiCashPayValuePropFactHeader2"),
      bodyText: t("uiCashPayValuePropFactBody2"),
    },
    {
      iconSrc: valuePropIcon3,
      headerText: t("uiCashPayValuePropFactHeader3"),
      bodyText: t("uiCashPayValuePropFactBody3"),
    },
  ];

  const { mutate: submitCashPayInfo } = useMutation(
    UserService.updateCashPayInfo,
    {
      onError: (err: Error) => {
        dispatch(errorActions.setError(err.message));
      },
      onSuccess: ({ formCompletedState, userState, error }) => {
        if (error) {
          dispatch(errorActions.setError(error));
        } else {
          if (userState) {
            dispatch(userActions.setUserState(userState));
          }
          if (userState === "MAINTENANCE_MODE") {
            return;
          }
          if (formCompletedState) {
            dispatch(
              formCompletionActions.setFormCompletion(formCompletedState)
            );
          }
          logAmplitudeEvent(
            "CASH_STATUS_PAGE",
            "cash_signup_card_successfully_submitted"
          );
        }
      },
    }
  );

  const handleSubmit = () => {
    logAmplitudeEvent("CASH_STATUS_PAGE", "cash_signup_card_clicked");
    // if cash pay agreement has not yet been submitted,
    // route merchant to Cash Pay Form to sign Cash Pay
    // agreement addendum and complete signing up for Cash App Pay
    if (!formCompletionState.cashPayAgreementAccepted) {
      setRoute(routes.cashForm);
      logAmplitudeEvent(
        "CASH_STATUS_PAGE",
        "cash_signup_card_routed_to_cash_form"
      );
    } else {
      const data = formatMerchantForCashPayInfo();
      logAmplitudeEvent(
        "CASH_STATUS_PAGE",
        "cash_signup_card_one_click_activation_submitted"
      );
      submitCashPayInfo(data);
    }
  };

  useRunOnce(() =>
    logAmplitudeEvent("CASH_STATUS_PAGE", "cash_signup_card_loaded")
  );

  const statusMap: StatusMap = {
    Done: {
      className: classes.dark,
    },
    InProgress: {
      className: classes.light,
    },
    None: {},
    Paused: {},
    Reviewing: {},
    RequiresAttention: {},
    Declined: {},
  };

  return (
    <StatusStep
      timeToFade={500}
      activeOnboardingStep="AddCashPay"
      stepStatus={currentState}
    >
      <Box
        className={`${statusMap[currentState].className} ${classes.statusWrapper}`}
        mb={2}
        p={2}
        data-testid="add-cash-pay-step"
      >
        <Box alignItems="center" display="flex" justifyContent="space-between">
          <Typography variant="h6">
            <strong>Activate Cash App Pay</strong>
          </Typography>
          <StatusLabel status={currentState} stepSpecificText={"Done"} />
        </Box>
        {currentState === "InProgress" && (
          <Box
            className={classes.addCashPayStepValuePropFactsWrapper}
            display="flex"
            flexDirection="column"
            justifyContent="center"
          >
            <Box
              className={classes.addCashPayStepValuePropFactWrapper}
              mt={1}
              mb={1}
              display="flex"
              flexDirection="column"
            >
              <Box mb={3}>
                <Typography variant="body2">
                  Cash App is the <strong>#1 finance app</strong> in the US app
                  store, attracting{" "}
                  <strong>51M+ monthly transacting actives*</strong> looking for
                  an easier way to send, spend, save and invest their money.
                </Typography>
              </Box>
              {valueProps.map(({ iconSrc, headerText, bodyText }, i) => (
                <AddCashPayStepValueProp
                  icon={<img src={iconSrc} alt={`icon${i}`} />}
                  header={headerText}
                  body={bodyText}
                  key={`value-prop-${i}`}
                />
              ))}
              <Box mt={2}>
                <Typography
                  className={classes.addCashPayStepValuePropFooterItem}
                >
                  * As of March 2023. A transacting active is a Cash App account
                  that has at least one financial transaction using any product
                  or service within Cash App during a specified period.
                </Typography>

                <Typography
                  className={classes.addCashPayStepValuePropFooterItem}
                >
                  ** Cash App assumes liability for consumer fraud as covered by
                  Regulation E.{" "}
                  <Link
                    className={classes.grayUnderlineLink}
                    href={CASH_PROGRAM_RULES_LINK}
                    rel="noopener noreferrer"
                    target="_blank"
                    onClick={() => {
                      logAmplitudeEvent(
                        "CASH_FORM",
                        "clicked_cash_program_rules_link"
                      );
                    }}
                  >
                    Some exclusions apply.
                  </Link>
                </Typography>
              </Box>
            </Box>
            <Box mt={2} data-testid="activate-cash-pay-button">
              <MopButton
                className={classes.addCashPayButton}
                mopVariant="primary"
                onClick={handleSubmit}
              >
                Activate
              </MopButton>
            </Box>
          </Box>
        )}
      </Box>
    </StatusStep>
  );
};
